import React, { useState } from 'react';

import {
  Box,
  Card,
  CardActions,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Typography,
  Link,
  Button,
  ClickAwayListener,
} from '@mui/material';
import { Tooltip, useMediaQuery, makeStyles } from '@material-ui/core';
import LockIcon from '@mui/icons-material/Lock';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

import { COLOR_PRIMARY, exchangeRates } from 'src/utils/const';
import { convertCurrency, formatCurrency } from 'src/utils/functions';
import { COLOR_GREY_600, COLOR_TEXT_2, COLOR_TEXT_3 } from 'src/utils/colors';

const useStylesRoot = makeStyles({
  tooltip: {
    padding: 0,
  },
});

const SubMarketCard = ({
  currency,
  subMarket,
  userhasDataAccess,
  handleOnSampleClick,
}: {
  currency: string;
  subMarket;
  userhasDataAccess: boolean;
  handleOnSampleClick;
}) => {
  const marketName = subMarket.submarket_name;
  const subMarketUrl = subMarket.submarket_url;
  const revenue = subMarket.revenue
    ? formatCurrency(String(convertCurrency(currency, subMarket.revenue)))
    : '-';
  const occupancyRate = subMarket.occupancy_rate || '-';
  const adr = subMarket.adr
    ? formatCurrency(String(convertCurrency(currency, subMarket.adr)))
    : '-';

  const currencySymbol = exchangeRates[currency].symbol;

  const kpis = [
    { value: `${currencySymbol}${revenue}`, title: 'Revenue Potential' },
    { value: `${occupancyRate}%`, title: 'Occupancy Rate' },
    { value: `${currencySymbol}${adr}`, title: 'Daily Rate' },
  ];

  return (
    <Card sx={{ width: '100%', maxWidth: '400px' }}>
      <CardContent>
        <Typography gutterBottom variant="body1" sx={{ fontWeight: 'bold' }}>
          {marketName}
        </Typography>
        <Grid container spacing={2} justifyContent={'center'} sx={{ mt: '0px' }} wrap="nowrap">
          {kpis.map((kpi, index) => (
            <>
              <Grid item flex={1}>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Typography
                    variant="body1"
                    sx={{ color: COLOR_TEXT_2, fontWeight: 'bold', fontSize: '14px' }}
                  >
                    {!userhasDataAccess ? (
                      <IconButton onClick={() => handleOnSampleClick('expert')}>
                        <LockIcon color="primary" />
                      </IconButton>
                    ) : (
                      <>{kpi.value}</>
                    )}
                  </Typography>
                  <Typography
                    variant="body2"
                    textAlign="center"
                    sx={{
                      color: COLOR_TEXT_3,
                      fontSize: '12px',
                    }}
                  >
                    {kpi.title}
                  </Typography>
                </Box>
              </Grid>

              {index < 2 && (
                <Grid item>
                  <Divider
                    orientation="vertical"
                    style={{ borderRightWidth: '1px', borderColor: COLOR_GREY_600 + '44' }}
                  />
                </Grid>
              )}
            </>
          ))}
        </Grid>
      </CardContent>
      <CardActions>
        <Link target="_blank" href={subMarketUrl} sx={{ width: '100%' }}>
          <Button
            fullWidth
            variant="contained"
            sx={{
              background: COLOR_PRIMARY + '22',
              color: COLOR_PRIMARY,
              borderRadius: '4px',
              '&:hover': {
                color: '#fff',
                background: COLOR_PRIMARY,
              },
            }}
          >
            Go to submarket <OpenInNewRoundedIcon fontSize="small" />
          </Button>
        </Link>
      </CardActions>
    </Card>
  );
};

const SubmarketPointer = ({
  lat,
  lng,
  submarket,
  currency,
  userhasDataAccess,
  handleOnSampleClick,
  showTooltip = true,
}) => {
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const matches = useMediaQuery('(max-width:550px)');
  const rootClass = useStylesRoot();

  const icon = (
    <Box
      className="discovery-score-card"
      sx={{
        position: 'relative',
        minWidth: '50px',
        width: '50vw',
        maxWidth: '135px',
        height: '35px',
        background: showTooltip ? '#ccc' : '#fff',
        color: showTooltip ? '#333' : '#000',
        borderRadius: 100,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxShadow: '0 2px 4px #0004, 0 2px 10px #0002',
        transform: 'translate(-50%, -50%)',
        transition: 'background 0.3s ease, color 0.3s ease',
        overflow: 'clip',
        '&:hover': showTooltip
          ? {
              background: '#000',
              color: '#fff',
            }
          : 'none',
      }}
    >
      <pre
        style={{
          paddingRight: '2px',
          textOverflow: 'ellipsis',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            minWidth: '40px',
            width: '50vw',
            maxWidth: '100px',
            overflow: 'clip',
            textOverflow: 'ellipsis',
          }}
        >
          {submarket.submarket_name}
        </Typography>
      </pre>
    </Box>
  );

  return showTooltip ? (
    <ClickAwayListener
      onClickAway={(event: MouseEvent | TouchEvent) => {
        const target: any = event.target as HTMLElement;
        const isTooltip = target.closest && target.closest('.MuiTooltip-tooltip');

        if (tooltipOpen && !isTooltip) {
          setTooltipOpen(false);
        }
      }}
    >
      <Tooltip
        interactive
        disableTouchListener
        disableFocusListener={matches ? true : false}
        classes={{
          tooltip: rootClass.tooltip,
        }}
        open={tooltipOpen}
        onOpen={() => setTooltipOpen(true)}
        onClose={() => {
          !matches && setTooltipOpen(false);
        }}
        style={{
          zIndex: 999999,
        }}
        enterTouchDelay={0}
        leaveTouchDelay={5000}
        title={
          <SubMarketCard
            currency={currency}
            subMarket={submarket}
            userhasDataAccess={userhasDataAccess}
            handleOnSampleClick={handleOnSampleClick}
          />
        }
      >
        {icon}
      </Tooltip>
    </ClickAwayListener>
  ) : (
    icon
  );
};

export default SubmarketPointer;
